import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  FileText, Download, Upload, CheckCircle, Clock, AlertTriangle,
  CreditCard,
} from "lucide-react";
import { Form } from "react-bootstrap";
import {
  PageHeader, AppButton, AppBadge, DetailRow,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import apiClient from "../../utils/apiClient";
import useRazorpay from "../../hooks/useRazorpay";
import "./institute-invoices.scss";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const STATUS_CONFIG = {
  sent: { label: "Sent", variant: "info" },
  viewed: { label: "Viewed", variant: "primary" },
  pending_payment: { label: "Pending Payment", variant: "warning" },
  paid: { label: "Paid", variant: "success" },
  overdue: { label: "Overdue", variant: "danger" },
  cancelled: { label: "Cancelled", variant: "secondary" },
};

const SERVICE_TYPE_LABELS = {
  custom_development: "Custom Development",
  integration: "Integration",
  support: "Support",
  amc: "AMC",
  training: "Training",
  others: "Others",
};

const InstituteInvoiceDetail = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Payment form state
  const [showPayForm, setShowPayForm] = useState(false);
  const [payForm, setPayForm] = useState({ transactionId: "", proofFile: null });
  const [submitting, setSubmitting] = useState(false);
  const { openCheckout, loading: rzpLoading } = useRazorpay();

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get(`/service-invoices/${id}`);
      setInvoice(data.invoice);
    } catch {
      setToast({ show: true, message: "Failed to load invoice.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoice(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownloadPdf = async () => {
    try {
      const response = await apiClient.get(`/service-invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      setToast({ show: true, message: "Failed to generate PDF.", type: "error" });
    }
  };

  const handleRazorpayPay = async () => {
    try {
      const { data } = await apiClient.post(`/service-invoices/${id}/pay/create-order`);
      await openCheckout(
        {
          razorpayKeyId: data.razorpayKeyId,
          order: data.order,
          student: {
            name: data.institute?.name || "",
            email: data.institute?.email || "",
            mobile: data.institute?.phone || "",
          },
          description: `Invoice ${data.invoice?.invoiceNumber || ""}`,
        },
        async (response) => {
          // Payment succeeded — verify on backend
          try {
            await apiClient.post(`/service-invoices/${id}/pay/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setToast({ show: true, message: "Payment successful!", type: "success" });
            fetchInvoice();
          } catch (err) {
            setToast({ show: true, message: err.response?.data?.message || "Payment verification failed.", type: "error" });
          }
        },
        (err) => {
          if (err?.reason !== "Payment cancelled by user.") {
            setToast({ show: true, message: err?.reason || "Payment failed.", type: "error" });
          }
        }
      );
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to initiate payment.", type: "error" });
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!payForm.transactionId.trim()) {
      setToast({ show: true, message: "Please enter a transaction ID.", type: "error" });
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append("transactionId", payForm.transactionId);
      if (payForm.proofFile) {
        formData.append("proofFile", payForm.proofFile);
      }

      await apiClient.post(`/service-invoices/${id}/pay/manual`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setToast({ show: true, message: "Payment proof submitted. Awaiting approval.", type: "success" });
      setShowPayForm(false);
      fetchInvoice();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to submit payment.", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="ii-page"><div className="ii-loading">Loading...</div></div>;
  }

  if (!invoice) {
    return <div className="ii-page"><div className="ii-loading">Invoice not found.</div></div>;
  }

  const sc = STATUS_CONFIG[invoice.status] || {};
  const canPay = ["sent", "viewed", "overdue"].includes(invoice.status);
  const isPendingApproval = invoice.payment?.approvalStatus === "pending";

  const billingFrom = invoice.billingFrom || {};
  const billingTo = invoice.billingTo || {};
  const hasTax = invoice.taxRate > 0 && invoice.taxAmount > 0;
  const isIntra = (invoice.cgstRate || 0) > 0;
  const isInter = (invoice.igstRate || 0) > 0;

  return (
    <div className="ii-page">
      <PageHeader
        backButton
        icon={FileText}
        title={`Invoice ${invoice.invoiceNumber}`}
        description={`Version ${invoice.version} • ${formatDate(invoice.createdAt)}`}
      />

      {/* Status Bar */}
      <div className="ii-detail-actions-bar">
        <AppBadge variant={sc.variant}>{sc.label}</AppBadge>
        <div className="ii-detail-actions">
          <AppButton variant="outline" size="sm" onClick={handleDownloadPdf}>
            <Download size={14} /> Download PDF
          </AppButton>
          {canPay && !isPendingApproval && (
            <>
              <AppButton size="sm" onClick={handleRazorpayPay} disabled={rzpLoading}>
                <CreditCard size={14} /> {rzpLoading ? "Processing..." : "Pay Now"}
              </AppButton>
              <AppButton variant="outline" size="sm" onClick={() => setShowPayForm(true)}>
                <Upload size={14} /> Manual Payment
              </AppButton>
            </>
          )}
        </div>
      </div>

      {/* Payment status messages */}
      {isPendingApproval && (
        <div className="ii-status-alert ii-status-warning">
          <Clock size={16} /> Your payment proof has been submitted and is awaiting approval.
        </div>
      )}
      {invoice.payment?.approvalStatus === "rejected" && (
        <div className="ii-status-alert ii-status-danger">
          <AlertTriangle size={16} /> Your payment was rejected.
          {invoice.payment.rejectionReason && <span> Reason: {invoice.payment.rejectionReason}</span>}
          <span> Please re-submit with correct details.</span>
        </div>
      )}
      {invoice.status === "overdue" && (
        <div className="ii-status-alert ii-status-danger">
          <AlertTriangle size={16} /> This invoice is overdue. Please make payment as soon as possible.
        </div>
      )}

      {/* Invoice Details */}
      <div className="ii-detail-grid">
        {billingFrom.companyName && (
          <div className="ii-detail-card">
            <h3><FileText size={16} /> From</h3>
            <DetailRow label="Company" value={billingFrom.companyName} />
            {billingFrom.address && <DetailRow label="Address" value={billingFrom.address} />}
            {billingFrom.state && (
              <DetailRow label="State" value={`${billingFrom.state}${billingFrom.stateCode ? ` (${billingFrom.stateCode})` : ""}`} />
            )}
            {billingFrom.pincode && <DetailRow label="Pincode" value={billingFrom.pincode} />}
            {billingFrom.gstin && <DetailRow label="GSTIN" value={billingFrom.gstin} />}
            {billingFrom.pan && <DetailRow label="PAN" value={billingFrom.pan} />}
          </div>
        )}

        {billingTo.name && (
          <div className="ii-detail-card">
            <h3><FileText size={16} /> Bill To</h3>
            <DetailRow label="Name" value={billingTo.name} />
            {billingTo.address && <DetailRow label="Address" value={billingTo.address} />}
            {billingTo.gstin && <DetailRow label="GSTIN" value={billingTo.gstin} />}
            {billingTo.email && <DetailRow label="Email" value={billingTo.email} />}
            {billingTo.phone && <DetailRow label="Phone" value={billingTo.phone} />}
          </div>
        )}

        <div className="ii-detail-card">
          <h3><FileText size={16} /> Invoice Details</h3>
          <DetailRow label="Service Type" value={SERVICE_TYPE_LABELS[invoice.serviceType] || invoice.serviceType} />
          {invoice.billingType && <DetailRow label="Billing Type" value={invoice.billingType} />}
          <DetailRow label="Due Date" value={formatDate(invoice.dueDate)} />
          {invoice.sacCode && <DetailRow label="SAC Code" value={invoice.sacCode} />}
          {invoice.placeOfSupply && <DetailRow label="Place of Supply" value={invoice.placeOfSupply} />}
          {invoice.reverseCharge !== undefined && <DetailRow label="Reverse Charge" value={invoice.reverseCharge ? "Yes" : "No"} />}
          <DetailRow label="Subtotal" value={formatCurrency(invoice.subtotal || invoice.totalAmount)} />
          {isIntra && (
            <>
              <DetailRow label={`CGST @ ${invoice.cgstRate}%`} value={formatCurrency(invoice.cgstAmount)} />
              <DetailRow label={`SGST @ ${invoice.sgstRate}%`} value={formatCurrency(invoice.sgstAmount)} />
              <DetailRow label="Total GST" value={formatCurrency((invoice.cgstAmount || 0) + (invoice.sgstAmount || 0))} />
            </>
          )}
          {isInter && (
            <DetailRow label={`IGST @ ${invoice.igstRate}%`} value={formatCurrency(invoice.igstAmount)} />
          )}
          {!isIntra && !isInter && hasTax && (
            <DetailRow label={`GST @ ${invoice.taxRate}%`} value={formatCurrency(invoice.taxAmount)} />
          )}
          {(invoice.roundedOff !== undefined && invoice.roundedOff !== 0) && (
            <DetailRow label="Rounded Off" value={`${invoice.roundedOff > 0 ? "+" : ""}${invoice.roundedOff.toFixed(2)}`} />
          )}
          <DetailRow label="Total Invoice Amount" value={formatCurrency(invoice.totalAmount)} />
          {invoice.sentAt && <DetailRow label="Sent" value={formatDate(invoice.sentAt)} />}
          {invoice.notes && <DetailRow label="Notes" value={invoice.notes} />}
        </div>
      </div>

      {/* Line Items */}
      <div className="ii-detail-card ii-line-items-detail">
        <h3>Line Items</h3>
        <table className="ii-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Description</th>
              <th>Qty</th>
              <th>Unit Price</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems?.map((item, idx) => (
              <tr key={item._id || idx}>
                <td>{idx + 1}</td>
                <td>{item.description}</td>
                <td>{item.quantity}</td>
                <td>{formatCurrency(item.unitPrice)}</td>
                <td>{formatCurrency(item.amount)}</td>
              </tr>
            ))}
            <tr className="ii-total-tr">
              <td colSpan={4} className="ii-total-label">Subtotal</td>
              <td className="ii-total-value">{formatCurrency(invoice.subtotal || invoice.totalAmount)}</td>
            </tr>
            {isIntra && (
              <>
                <tr className="ii-gst-tr">
                  <td colSpan={4} className="ii-total-label">CGST @ {invoice.cgstRate}%</td>
                  <td className="ii-total-value">{formatCurrency(invoice.cgstAmount)}</td>
                </tr>
                <tr className="ii-gst-tr">
                  <td colSpan={4} className="ii-total-label">SGST @ {invoice.sgstRate}%</td>
                  <td className="ii-total-value">{formatCurrency(invoice.sgstAmount)}</td>
                </tr>
              </>
            )}
            {isInter && (
              <tr className="ii-gst-tr">
                <td colSpan={4} className="ii-total-label">IGST @ {invoice.igstRate}%</td>
                <td className="ii-total-value">{formatCurrency(invoice.igstAmount)}</td>
              </tr>
            )}
            {!isIntra && !isInter && hasTax && (
              <tr className="ii-gst-tr">
                <td colSpan={4} className="ii-total-label">GST @ {invoice.taxRate}%</td>
                <td className="ii-total-value">{formatCurrency(invoice.taxAmount)}</td>
              </tr>
            )}
            {(invoice.roundedOff !== undefined && invoice.roundedOff !== 0) && (
              <tr className="ii-gst-tr">
                <td colSpan={4} className="ii-total-label">Rounded Off</td>
                <td className="ii-total-value">{invoice.roundedOff > 0 ? "+" : ""}{invoice.roundedOff.toFixed(2)}</td>
              </tr>
            )}
            <tr className="ii-grand-total-tr">
              <td colSpan={4} className="ii-total-label">Total Invoice Amount</td>
              <td className="ii-total-value">{formatCurrency(invoice.totalAmount)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Payment Info (if paid) */}
      {invoice.status === "paid" && invoice.payment && (
        <div className="ii-detail-card ii-paid-card">
          <h3><CheckCircle size={16} /> Payment Confirmed</h3>
          <DetailRow label="Method" value={invoice.payment.method || "—"} />
          {invoice.payment.transactionId && <DetailRow label="Transaction ID" value={invoice.payment.transactionId} />}
          {invoice.payment.paidAt && <DetailRow label="Paid At" value={formatDate(invoice.payment.paidAt)} />}
        </div>
      )}

      {/* Manual Payment Form */}
      {showPayForm && (
        <div className="ii-detail-card ii-pay-form-card">
          <h3><Upload size={16} /> Submit Manual Payment</h3>
          <p className="ii-pay-info">
            Pay the full amount of <strong>{formatCurrency(invoice.totalAmount)}</strong> via bank transfer or UPI, then submit the details below.
          </p>
          <form onSubmit={handleSubmitPayment}>
            <div className="ii-pay-field">
              <label>Transaction ID / Reference Number *</label>
              <Form.Control
                type="text"
                value={payForm.transactionId}
                onChange={(e) => setPayForm((prev) => ({ ...prev, transactionId: e.target.value }))}
                placeholder="Enter transaction ID"
                required
              />
            </div>
            <div className="ii-pay-field">
              <label>Payment Proof (screenshot/receipt)</label>
              <Form.Control
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={(e) => setPayForm((prev) => ({ ...prev, proofFile: e.target.files[0] || null }))}
              />
              <small className="ii-pay-hint">Accepted: JPG, PNG, WebP, PDF (max 10MB)</small>
            </div>
            <div className="ii-pay-actions">
              <AppButton variant="outline" type="button" onClick={() => setShowPayForm(false)}>
                Cancel
              </AppButton>
              <AppButton type="submit" disabled={submitting}>
                <Upload size={14} /> {submitting ? "Submitting..." : "Submit Payment Proof"}
              </AppButton>
            </div>
          </form>
        </div>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: "success" })}
      />
    </div>
  );
};

export default InstituteInvoiceDetail;
